import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import cloneDeep from "lodash/cloneDeep";

import Multimedia from "@sharedComponents/multimedia";
import { InternalButton } from "@sharedComponents/buttons";

import UnavailableItemCard from "@components/unavailable-item-card";
import { useStoreContext } from "@components/store/provider";

import {
  getCartOrderId,
  getCartOrderLineItems,
  getCartOrderMedleySets,
  getCartOrderProductSets,
} from "@selectors/cart";

import {
  findItemIndex,
  findMedleyItemIndex,
  findUnavailableItems,
  mapRemovedItemsWithZeroQuantity,
  mergeUnavailableItems,
  mergeUnavailableMedleyItems,
  updateMedleyComponents,
} from "@helpers/unavailable-items";

import usePopulateItems from "@hooks/cart/use-populate-items";

import { getCartRoute } from "@constants/routes";

import styles from "./main.module.scss";

import NoImage from "@assets/images/no-image.svg";
import useRouter from "@stadium/hooks/lib/use-custom-route";

const emptyArr = [];
const DEFAULT_DETAILS = {
  lineItems: emptyArr,
  productSets: emptyArr,
  medleySets: emptyArr,
};

const LINE_ITEM_TYPE = "lineItems";
const PRODUCT_SET_TYPE = "productSets";
const MEDLEY_SET_TYPE = "medleySets";
const MEDLEY_COMPONENT_TYPE = "medleyComponents";

const useUnavailableItemsModal = () => {
  const { push } = useRouter();
  const { storeBasePath } = useStoreContext();

  const cartLineItems = useSelector(getCartOrderLineItems);
  const cartProductSets = useSelector(getCartOrderProductSets);
  const cartMedleySets = useSelector(getCartOrderMedleySets);

  const orderId = useSelector(getCartOrderId);

  const [isOpen, setIsOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const [cartData, setCartData] = useState({});
  const [unavailableItems, setUnavailableItems] = useState(DEFAULT_DETAILS);

  const { updateMultipleItemsInCart } = usePopulateItems();

  useEffect(() => {
    setCartData(cloneDeep(unavailableItems));
  }, [unavailableItems]);

  const toggle = () => setIsOpen(!isOpen);

  const openUnavailableItemsModal = ({
    unavailableLineItems = {},
    unavailableProductSets = {},
    unavailableMedleyItems = {},
  }) => {
    const lineItems = mergeUnavailableItems(cartLineItems, unavailableLineItems);
    const productSets = mergeUnavailableItems(cartProductSets, unavailableProductSets);
    const medleySets = mergeUnavailableMedleyItems({
      cartMedleySets,
      unavailableMedleyItems,
      unavailableLineItems,
    });
    setUnavailableItems({ lineItems, medleySets, productSets });
    toggle();
  };

  const { lineItems = emptyArr, medleySets = emptyArr, productSets = emptyArr } = cartData;

  const hasAnyUnavailableItems = useMemo(
    () => lineItems?.length > 0 || medleySets?.length > 0 || productSets?.length > 0,
    [cartData],
  );

  const updateBagEnabled = useMemo(() => {
    if (isEmpty(cartData)) return true;
    const { lineItems, medleySets, productSets } = cartData;
    return (
      lineItems.every(({ quantity, availableQuantity }) => quantity <= availableQuantity) &&
      medleySets.every(
        ({ quantity, availableQuantity, medleyComponents }) =>
          (!availableQuantity || quantity <= availableQuantity) &&
          medleyComponents.every(
            ({ quantity, availableQuantity }) => quantity <= availableQuantity,
          ),
      ) &&
      productSets.every(({ quantity, availableQuantity }) => quantity <= availableQuantity)
    );
  }, [cartData]);

  const onChangeQuantity = (details, itemType, medleyDetails = details) => {
    const { productId, storeId, quantity, medleyItemSetId } = details;

    if (itemType === MEDLEY_COMPONENT_TYPE) {
      const { id: medleyId } = medleyDetails;
      const ind = findMedleyItemIndex(cartData.medleySets, medleyId);
      const componentIndex = findMedleyItemIndex(
        cartData.medleySets[ind].medleyComponents,
        medleyItemSetId,
      );

      cartData.medleySets[ind].medleyComponents[componentIndex].quantity = quantity;
    } else {
      const ind = findItemIndex(cartData[itemType], productId, storeId);
      cartData[itemType][ind].quantity = quantity;
    }

    setCartData({ ...cartData });
  };

  const removeProductFromCart = (details, itemType, medleyDetails = details) => {
    const { productId, storeId, id: medleyComponentId } = details;

    if (itemType === MEDLEY_COMPONENT_TYPE) {
      const { id: medleyId } = medleyDetails;
      const ind = findMedleyItemIndex(cartData.medleySets, medleyId);
      const componentIndex = findMedleyItemIndex(
        cartData.medleySets[ind].medleyComponents,
        medleyComponentId,
      );

      cartData.medleySets[ind].medleyComponents.splice(componentIndex, 1);
      if (cartData.medleySets[ind].medleyComponents.length === 0) {
        cartData.medleySets.splice(ind, 1);
      }
    } else {
      const ind = findItemIndex(cartData[itemType], productId, storeId);
      cartData[itemType].splice(ind, 1);
    }

    setCartData({ ...cartData });
  };

  const updateBag = () => {
    setIsRemoving(true);

    const removedLineItems = findUnavailableItems(lineItems, unavailableItems.lineItems);
    const updatedLineItems = [...lineItems, ...mapRemovedItemsWithZeroQuantity(removedLineItems)];

    const removedProductSets = findUnavailableItems(productSets, unavailableItems.productSets);
    const updatedProductSets = [
      ...productSets,
      ...mapRemovedItemsWithZeroQuantity(removedProductSets),
    ];

    const removedMedleySets = updateMedleyComponents(medleySets, unavailableItems.medleySets);
    const updatedMedleySets = [
      ...medleySets,
      ...mapRemovedItemsWithZeroQuantity(removedMedleySets),
    ];

    updateMultipleItemsInCart(
      {
        lineItems: updatedLineItems,
        productSets: updatedProductSets,
        medleySets: updatedMedleySets,
      },
      orderId,
    ).then(() => {
      setIsRemoving(false);
      setIsOpen(false);
      push(getCartRoute({ storeBasePath }));
    });
  };

  const renderUnavailableCard = (item, type, medleyDetails) => (
    <UnavailableItemCard
      key={item.id}
      lineItem={item}
      onChangeQuantity={(updatedQuantity) => onChangeQuantity(updatedQuantity, type, medleyDetails)}
      removeProductFromCart={(updatedQuantity) =>
        removeProductFromCart(updatedQuantity, type, medleyDetails)
      }
    />
  );

  const renderMedleyComponent = (item) => (
    <div key={item.id} className={styles.itemRow}>
      <div>
        <Multimedia
          type="image"
          src={item.images?.[0] || NoImage}
          alt="Not Found"
          height={60}
          width={60}
        />
      </div>
      <div className={styles.itemCol}>
        <div className={styles.name}>{item.name}</div>
        <div className={styles.qty}>Quantity: {item.quantity}</div>
      </div>
      {item.medleyComponents.map((component) =>
        renderUnavailableCard(component, MEDLEY_COMPONENT_TYPE, item),
      )}
    </div>
  );

  const renderUnavailableItemsModal = () => {
    return (
      <Modal
        data-acsb-overlay="popup"
        show={isOpen}
        animation={false}
        className={`${styles.generalModal} ${styles.unavailableItemModal} `}
        dialogClassName={`${styles.modalDialog}`}
      >
        <Modal.Header className={`${styles.header} ${styles.header}`}>
          <div>
            <h1 className={`${styles.modalHeading}`}>Update for items in your bag</h1>
            <p className={styles.renderInfo}>
              Some items in your bag are either out of stock or low in stock. Please edit the
              following items before you check out.
            </p>
          </div>
          <InternalButton styleType="link" className={`${styles.closeIcon}`} onClick={toggle} />
        </Modal.Header>
        {hasAnyUnavailableItems && (
          <Modal.Body className={`${styles.modalBody}`}>
            <>
              {lineItems.map((item) => renderUnavailableCard(item, LINE_ITEM_TYPE))}
              {productSets.map((item) => renderUnavailableCard(item, PRODUCT_SET_TYPE))}
              {medleySets.map((item) => {
                if (item.medleyComponents.length) return renderMedleyComponent(item);
                return renderUnavailableCard(item, MEDLEY_SET_TYPE);
              })}
            </>
          </Modal.Body>
        )}
        <Modal.Footer className={`${styles.modalFooter} ${styles.mobileFixedFooter}`}>
          <InternalButton
            className={styles.actionButton}
            onClick={updateBag}
            isLoading={isRemoving}
            disabled={!updateBagEnabled}
          >
            Update Bag
          </InternalButton>
        </Modal.Footer>
      </Modal>
    );
  };

  return { openUnavailableItemsModal, renderUnavailableItemsModal };
};

export default useUnavailableItemsModal;
